import { cloneDeep } from 'lodash';
/**
 * Peeks the current result of an execution chain without affecting the output result.
 *
 * @param fn Peeking function that processes input values without interfering with the {@link peek} output.
 * @returns The first input value.
 *
 * @example
 * const result = (['a', 'b', 'c'] as const).map(peek((value: unknown, i, array) => {
 *   console.log(`"${value}" at index ${i}`);
 *   console.log(`last value ${array[array.lastIndexOf()]}`);
 *   return 'd';
 * }));
 */
export const peek = (fn) => (value, ...otherArgs) => {
    fn(cloneDeep(value), ...cloneDeep(otherArgs));
    return value;
};
/**
 * Spreads array input argument into provided function arguments. Mostly useful when processing tuples, e.g. object entries.
 *
 * @param fn Function to be invoked with the spread arguments.
 * @returns The result of the invoked function.
 *
 * @example
 * const obj = {
 *   foo: 12,
 * };
 *
 * function process(name: string, count: number) {
 *   // ...
 * }
 *
 * Object.entries(obj)
 *   .map(spread(process));
 */
export const spread = (fn) => (array) => fn(...array);
/**
 * Converts an input value to a key-value pair using provided key and value functions.
 *
 * @template T The type of the input value.
 * @template U The type of additional arguments passed to the key and value functions.
 * @template V The type of the value in the resulting key-value pair (defaults to the type of the input value).
 * @template K The type of the key in the resulting key-value pair (defaults to the type of the input value).
 *
 * @param options Object containing key and value functions.
 *   @param options.key The function to extract the key from the input.
 *   @param options.value The function to extract the value from the input.
 * @returns A function that takes an input value and additional arguments,
 *   and returns a key-value pair.
 *
 * @example
 * const toKeyValue = toEntry({
 *   key: (input) => input.id,
 *   value: (input) => input.name,
 * });
 *
 * const result = toKeyValue({ id: 1, name: 'John' });
 * // Result: [1, 'John']
 */
export const toEntry = ({ key, value }) => (input, ...otherArgs) => [
    key(cloneDeep(input), ...cloneDeep(otherArgs)),
    value(cloneDeep(input), ...cloneDeep(otherArgs)),
];
/**
 * Calls a function with a provided value.
 *
 * @template T The type of the provided value.
 * @template U The return type of the function being called.
 *
 * @param value The value to be passed to the calling function.
 * @returns A function that takes a calling function and returns the result of the call.
 *
 * @example
 * [
 *   (value: number) => value * 2,
 *   (value: unknown) => console.log(String(value)),
 * ].map(callWith(2));
 */
export const callWith = (value) => (call) => call(value);
//# sourceMappingURL=mappers.js.map